import React, { useEffect, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Stack,
    Typography,
} from "@mui/material";
import { Add } from "@mui/icons-material";

import { IRelease } from "../../interfaces/IRelease";
import { IChange } from "../../interfaces/IChange";
import { getTmpId } from "../core/helper";
import { IChangeType } from "../../interfaces/IChangeType";
import { ChangeAccordion } from "./ChangeAccordion";

export interface IProps {
    currentRelease: IRelease;
    setCurrentRelease: Function;
    changeTypeArray: IChangeType[];
}

export const getEmptyChange = (idRelease: number, changesArray: IChange[], changeType?: number) => {
    return {
        ChangeType: changeType ?? 1,
        descriptionHtml: "",
        dinoAcademyLink: null,
        id: getTmpId(changesArray),
        isHighlighted: false,
        Release: idRelease,
        title: "",
    } as IChange;
};

export const ChangeSite: React.FC<IProps> = ({
    currentRelease,
    setCurrentRelease,
    changeTypeArray,
}) => {
    const [changeArray, setChangeArray] = useState(currentRelease.changes);

    const handleAddNew = (changeType?: number) => {
        setChangeArray([...changeArray, getEmptyChange(currentRelease.id, changeArray, changeType)]);
    };

    /** Eintrag um eine Position verschieben — bestimmt die Reihenfolge im Changelog. */
    const handleMove = (index: number, direction: -1 | 1) => {
        const target = index + direction;
        if (target < 0 || target >= changeArray.length) return;
        const next = [...changeArray];
        const [moved] = next.splice(index, 1);
        next.splice(target, 0, moved);
        setChangeArray(next);
    };

    const handleDuplicate = (change: IChange) => {
        const copy = {
            ...change,
            id: getTmpId(changeArray),
            title: `${change.title} (Kopie)`,
        } as IChange;
        setChangeArray([...changeArray, copy]);
    };

    useEffect(() => {
        setCurrentRelease({
            ...currentRelease,
            changes: changeArray,
        });
    }, [changeArray]);

    const missingTitles = changeArray.filter(x => (x.title || "").trim() === "").length;
    const highlights = changeArray.filter(x => x.isHighlighted).length;

    return (
        <Card variant="outlined" sx={{ mt: 2 }}>
            <CardContent>
                <Stack
                    direction="row"
                    spacing={1}
                    sx={{ alignItems: "center", justifyContent: "space-between", mb: 2 }}
                >
                    <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                        <Typography variant="h6">Eintragungen</Typography>
                        <Chip size="small" label={`${changeArray.length} gesamt`} />
                        {highlights > 0 && (
                            <Chip
                                size="small"
                                color="warning"
                                label={`${highlights} Highlight${highlights === 1 ? "" : "s"}`}
                            />
                        )}
                    </Stack>
                    <Button variant="outlined" startIcon={<Add />} onClick={() => handleAddNew()}>
                        Neuer Eintrag
                    </Button>
                </Stack>

                {changeTypeArray.length > 1 && (
                    <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: "wrap", rowGap: 1 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
                            Schnell anlegen:
                        </Typography>
                        {changeTypeArray.map(changeType => (
                            <Chip
                                key={`quickadd-${changeType.id}`}
                                size="small"
                                variant="outlined"
                                clickable
                                label={changeType.changeType}
                                onClick={() => handleAddNew(changeType.id)}
                            />
                        ))}
                    </Stack>
                )}

                {missingTitles > 0 && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        {missingTitles === 1
                            ? "Ein Eintrag hat noch keinen Titel."
                            : `${missingTitles} Einträge haben noch keinen Titel.`}
                    </Alert>
                )}

                {changeArray.length === 0 && (
                    <Alert severity="info">
                        Noch keine Eintragungen. Legen Sie je Änderung einen Eintrag an — die
                        Reihenfolge hier ist die Reihenfolge im Changelog.
                    </Alert>
                )}

                <Box>
                    {changeArray.map((change, index) => (
                        <ChangeAccordion
                            key={`edit-change-${change.id}`}
                            changeArray={changeArray}
                            changeTypeArray={changeTypeArray}
                            currentChange={change}
                            setChangeArray={setChangeArray}
                            index={index}
                            onMove={handleMove}
                            onDuplicate={handleDuplicate}
                        />
                    ))}
                </Box>
            </CardContent>
        </Card>
    );
};
